import React from 'react';
import { Doctor, Staff, ScheduleItem, Appointment, Room, Task } from './SchedulerTypes';
import { TableCell } from '@/components/ui/table';
import { isTimeWithinRange } from '@/lib/timeUtils';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useCopyPaste } from '@/contexts/CopyPasteContext';
import { useToast } from '@/hooks/use-toast';
import { Copy, Clipboard, Trash2 } from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { getTintedBackground } from '@/lib/colorUtils';

interface TimeSlotCellProps {
  time: string;
  personId: string;
  isDoctor?: boolean;
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  getAppointmentForScheduleItem: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem: (scheduleItemId: string) => Task | undefined;
  onCellClick: (time: string, personId: string, isDoctor?: boolean) => void;
  onEditItem: (item: ScheduleItem) => void;
  onBookAppointment: (schedule: ScheduleItem) => void;
  onViewAppointment: (appointment: Appointment) => void;
  onAssignTask: (schedule: ScheduleItem) => void;
  onViewTask: (task: Task) => void;
  onAddScheduleItem?: (item: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'>) => void;
  onDeleteScheduleItem?: (id: string) => void;
  currentDate?: string;
  onShowMultiItemSelection?: (items: ScheduleItem[], time: string) => void;
}

const TimeSlotCell = ({
  time,
  personId,
  isDoctor,
  scheduleItems,
  doctors,
  staff,
  rooms,
  getAppointmentForScheduleItem,
  onCellClick,
  onEditItem,
  onAddScheduleItem,
  onDeleteScheduleItem,
  currentDate,
  onShowMultiItemSelection,
}: TimeSlotCellProps) => {
  const { copyScheduleItem, copiedItem, hasCopiedItem } = useCopyPaste();
  const { toast } = useToast();
  const { currentClinic } = useClinic();
  // Filter items for this person and time slot
  const personScheduleItems = scheduleItems.filter(item => {
    const personMatches = isDoctor ? item.doctorId === personId : item.staffId === personId;
    return personMatches;
  });

  // Filter by time range using the utility function
  const timeSlotItems = personScheduleItems.filter(item => {
    const timeInRange = isTimeWithinRange(time, item.startTime, item.endTime);
    return timeInRange;
  });

  const handleCellClick = () => {
    if (timeSlotItems.length === 0) {
      // If no item, create new one
      onCellClick(time, personId, isDoctor);
    } else if (timeSlotItems.length === 1) {
      // If there's exactly one item, edit it directly
      onEditItem(timeSlotItems[0]);
    } else {
      // Multiple items - show selection modal
      if (onShowMultiItemSelection) {
        onShowMultiItemSelection(timeSlotItems, time);
      } else {
        // Fallback: edit the first item if no modal handler
        onEditItem(timeSlotItems[0]);
      }
    }
  };

  const handleCopy = (item: ScheduleItem) => {
    console.log('[TimeSlotCell] Copying item:', item);
    copyScheduleItem(item);
  };

  const handlePaste = () => {
    console.log('[TimeSlotCell] handlePaste called', { copiedItem, onAddScheduleItem, currentDate });
    
    if (!copiedItem) {
      console.log('[TimeSlotCell] No copied item');
      return;
    }
    
    if (!onAddScheduleItem) {
      console.log('[TimeSlotCell] onAddScheduleItem is not defined');
      toast({
        title: "Error",
        description: "Unable to paste. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }

    // Calculate duration of copied item
    const [copiedStartHour, copiedStartMin] = copiedItem.startTime.split(':').map(Number);
    const [copiedEndHour, copiedEndMin] = copiedItem.endTime.split(':').map(Number);
    const durationMinutes = (copiedEndHour * 60 + copiedEndMin) - (copiedStartHour * 60 + copiedStartMin);

    // Calculate new end time based on clicked time slot
    const [targetHour, targetMin] = time.split(':').map(Number);
    const newEndTotalMinutes = targetHour * 60 + targetMin + durationMinutes;
    const newEndHour = Math.floor(newEndTotalMinutes / 60);
    const newEndMin = newEndTotalMinutes % 60;

    // Check if new end time exceeds day boundaries
    if (newEndHour >= 24) {
      toast({
        title: "Cannot paste",
        description: "The pasted item would exceed the day boundary.",
        variant: "destructive",
      });
      return;
    }

    const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;

    // Create new schedule item with adjusted properties
    // Remove id, created_at, and updated_at from the copied item
    const { id, created_at, updated_at, ...copiedItemWithoutMeta } = copiedItem;
    
    const newItem: Omit<ScheduleItem, 'id'> = {
      ...copiedItemWithoutMeta,
      startTime: time,
      endTime: newEndTime,
      doctorId: isDoctor ? personId : (copiedItem.doctorId || undefined),
      staffId: !isDoctor ? personId : (copiedItem.staffId || undefined),
      date: currentDate || copiedItem.date,
      clinic_id: currentClinic?.id || copiedItem.clinic_id,
    };

    console.log('[TimeSlotCell] Creating new item:', newItem);

    try {
      // Add the new schedule item directly
      onAddScheduleItem(newItem);
      
      toast({
        title: "Pasted",
        description: `Schedule item pasted from ${time} to ${newEndTime}`,
      });
    } catch (error) {
      console.error('[TimeSlotCell] Error pasting item:', error);
      toast({
        title: "Error",
        description: "Failed to paste schedule item. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getBackgroundColor = (item: ScheduleItem) => {
    const appointment = getAppointmentForScheduleItem(item.id);

    // Color coding based on schedule type
    if (item.type === 'mc') {
      // MC/Urgent Leave: Dark Red
      return { className: 'bg-red-700 text-white', style: {} };
    }
    if (item.type === 'leave') {
      // Leave (Planned): Red
      return { className: 'bg-red-500 text-white', style: {} };
    }
    if (item.type === 'admin') {
      // Admin: Yellow
      return { className: 'bg-yellow-100 text-yellow-800', style: {} };
    }
    if (item.type === 'break') {
      // Break: Grey
      return { className: 'bg-gray-100 text-gray-800', style: {} };
    }
    
    // Check for appointment type (simplified condition)
    if (item.type === 'appointment') {
      // Check if appointment has a room with color (handle empty string properly)
      if (item.roomId !== undefined && item.roomId !== null && item.roomId !== '' && rooms && rooms.length > 0) {
        const room = rooms.find(r => r.id === item.roomId);

        if (room?.color && room.color !== '#CBD5E0' && room.color !== '') { // Ignore default gray color and empty strings
          // Use room color for text and tinted background with stronger visibility
          const backgroundColor = `${room.color}33`; // 20% opacity in hex

          return {
            className: '',
            style: {
              backgroundColor: backgroundColor,
              color: room.color,
              borderTop: `2px solid ${room.color}`,
              borderRight: `2px solid ${room.color}`,
              borderBottom: `2px solid ${room.color}`,
              borderLeft: `2px solid ${room.color}`,
              fontWeight: '600',
              boxSizing: 'border-box' as const,
              padding: 0, // Remove padding to let border extend to edges
            }
          };
        }
      }
      // Default appointment color: Green
      return { className: 'bg-green-100 text-green-800', style: {} };
    }

    // Default fallback
    return { className: 'bg-green-100 text-green-800', style: {} };
  };

  const getItemTitle = (item: ScheduleItem) => {
    const appointment = getAppointmentForScheduleItem(item.id);
    if (appointment) {
      // For appointments, show patient name and assigned personnel
      const assignedDoctor = item.doctorId ? doctors.find(d => d.id === item.doctorId) : null;
      const assignedStaff = item.staffId ? staff.find(s => s.id === item.staffId) : null;
      
      let title = appointment.patientName;
      
      // Check if this is a backup assignment
      const isBackupAssignment = item.notes?.includes('Auto-reassigned from');
      const backupInfo = isBackupAssignment ? item.notes?.match(/\(Preference #(\d)\)/) : null;
      
      // Add assigned personnel info if viewing from a different person's column
      if (item.doctorId && item.staffId) {
        // Both doctor and staff assigned
        if (isDoctor && personId === item.doctorId && assignedStaff) {
          // Doctor viewing, show staff short name without parentheses
          let staffDisplay = assignedStaff.short_name || assignedStaff.name;
          // Add backup indicator if this is a backup assignment
          if (isBackupAssignment && backupInfo) {
            staffDisplay += ` (Backup #${backupInfo[1]})`;
          }
          title += `\n${staffDisplay}`;
        } else if (!isDoctor && personId === item.staffId && assignedDoctor) {
          // Staff viewing, show doctor info with Dr prefix
          let doctorDisplay = `(w/ Dr ${assignedDoctor.short_name || assignedDoctor.name})`;
          // Add backup indicator if this is a backup assignment
          if (isBackupAssignment && backupInfo) {
            doctorDisplay += ` - Backup`;
          }
          title += `\n${doctorDisplay}`;
        }
      }
      
      return title;
    }
    return item.title;
  };

  // Get person name for ARIA labels
  const personName = isDoctor 
    ? doctors.find(d => d.id === personId)?.name || 'Doctor'
    : staff.find(s => s.id === personId)?.name || 'Staff';

  if (timeSlotItems.length > 0) {
    const item = timeSlotItems[0];
    const colorStyle = getBackgroundColor(item);
    // Check if we have custom border styling (room colors)
    const hasCustomBorder = colorStyle.style && colorStyle.style.borderTop;
    const hasMultipleItems = timeSlotItems.length > 1;

    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <TableCell
            className={`${hasCustomBorder ? '' : 'p-2'} cursor-pointer transition-all hover:opacity-80 ${colorStyle.className} relative`}
            style={colorStyle.style}
            onClick={handleCellClick}
            role="button"
            tabIndex={0}
            aria-label={`${personName} at ${time}: ${getItemTitle(item)}${hasMultipleItems ? ` and ${timeSlotItems.length - 1} more items` : ''}. Click to ${hasMultipleItems ? 'select an item' : 'edit or view details'}.`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCellClick();
              }
            }}
          >
            {/* Count badge for multiple items */}
            {hasMultipleItems && (
              <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm z-10">
                {timeSlotItems.length}
              </div>
            )}
            <div className={`text-xs font-medium text-center whitespace-pre-line ${hasCustomBorder ? 'p-2' : ''}`}>
              {getItemTitle(item)}
            </div>
          </TableCell>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopy(item);
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy Schedule Item
          </ContextMenuItem>
          {onDeleteScheduleItem && (
            <ContextMenuItem 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDeleteScheduleItem(item.id);
              }}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Schedule
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TableCell
          className="p-2 border border-border hover:bg-accent/50 transition-colors cursor-pointer text-center"
          onClick={handleCellClick}
          role="button"
          tabIndex={0}
          aria-label={`${personName} at ${time}: Available. Click to schedule an appointment or task.`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCellClick();
            }
          }}
        >
          <div className="text-xs text-muted-foreground">Available</div>
        </TableCell>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {hasCopiedItem ? (
          <ContextMenuItem 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePaste();
            }}
          >
            <Clipboard className="mr-2 h-4 w-4" />
            Paste Schedule Item
          </ContextMenuItem>
        ) : (
          <ContextMenuItem disabled>
            <Clipboard className="mr-2 h-4 w-4 opacity-50" />
            No item copied
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default TimeSlotCell;
